import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Html5Qrcode } from 'html5-qrcode';
import '../../../scss/MaterialRequestScanner.scss';
import routes from '../../../Routes/routes';
import { errorMsg } from '../../../Apis/functions';
import { DecryptMessage } from '../../../reduxToolkit/EnDecrypt/EnDecryptApi';
import { isVehicleMovement } from '../../Dashboard/MaterialControl/Model/Model';
import {
    QR_CODE_TYPE,
    SCANNER_CONFIG,
    SCANNER_PREVIEW_PLAY_TIMEOUT,
    SCANNER_REGION_ID,
    SCANNER_STATUS,
    SCANNER_STATUS_ACTIONS,
    UUID_REGEX,
} from '../Model/Model';

/**
 * @Author: Karen Yunuen Pozos Arias
 * @JobTitle: Full Stack Developer - IBL
 * @Email: kpozos@ibl.mx
 */

/**
 * View each accepted QR code type is opened in, completed with the decrypted identifier.
 *
 * A material request is dispatched, while a material control is read in the
 * detail the guard registers its gate movement with.
 */
const ROUTE_BY_QR_CODE_TYPE = {
    [QR_CODE_TYPE.MATERIAL_REQUEST]: routes.materialRequest.dispatch,
    [QR_CODE_TYPE.MATERIAL_CONTROL]: routes.entryAndExitForms.details,
    [QR_CODE_TYPE.VEHICLE_MOVEMENT]: routes.vehicleMovements.details,
};

/**
 * Reads the encrypted identifier and its type out of a scanned QR code.
 *
 * The QR body is the one built by the backend (QRCodeBody), so the identifier
 * travels encrypted inside the `data` field and only the material request and
 * material control types are accepted here.
 *
 * @param {string} decodedText Raw text read from the camera.
 * @returns {{type: number, data: string}|null} Type and encrypted identifier, or null when the QR is not accepted.
 */
const readQrCodeBody = (decodedText) => {
    try {
        const qrBody = JSON.parse(decodedText);

        if (!ROUTE_BY_QR_CODE_TYPE[qrBody?.type] || !qrBody?.data) {
            return null;
        }

        return { type: qrBody.type, data: qrBody.data };
    } catch (error) {
        return null;
    }
};

/**
 * Waits until the camera preview is playing.
 *
 * html5-qrcode resolves its start as soon as the video is mounted, while the
 * play() the preview is shown with is left running on its own. Removing the
 * video before that play() finishes interrupts it, so the camera is only
 * considered started once the preview plays, and the wait is given up on after
 * SCANNER_PREVIEW_PLAY_TIMEOUT so a video that never starts does not lock it.
 *
 * @returns {Promise} Promise resolved once the preview is playing.
 */
const waitForPreviewPlaying = () => new Promise((resolve) => {
    const video = document.getElementById(SCANNER_REGION_ID)?.querySelector('video');

    // HAVE_FUTURE_DATA onwards the video already plays, so there is nothing to wait for.
    if (!video || video.readyState >= 3) {
        resolve();
        return;
    }

    const finishWait = () => {
        clearTimeout(timeoutId);
        video.removeEventListener('playing', finishWait);
        resolve();
    };

    const timeoutId = setTimeout(finishWait, SCANNER_PREVIEW_PLAY_TIMEOUT);
    video.addEventListener('playing', finishWait);
});

export const MaterialRequestScannerApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState(SCANNER_STATUS.IDLE);
    const [showSpinner, setShowSpinner] = useState(false);
    // The gate looks the folio up by hand when the QR code cannot be scanned, so
    // the camera is released while the folio is being looked for.
    const [showManualFolio, setShowManualFolio] = useState(false);
    const [selectedFolio, setSelectedFolio] = useState(null);
    // The camera instance lives in a ref because the html5-qrcode callbacks are
    // registered once and would otherwise read a stale state value.
    const scannerRef = useRef(null);
    // A single QR can be decoded several times per second, so the read is locked
    // while the identifier is being resolved.
    const processingRef = useRef(false);
    // Starting and stopping the camera are chained one after the other, because
    // stopping it while it is still starting removes the preview from the
    // document and interrupts the play() the camera is shown with.
    const cameraQueueRef = useRef(Promise.resolve());

    /**
     * Chains a camera operation after the one that is still running.
     *
     * @param {Function} task Operation the camera is started or stopped with.
     * @returns {Promise} Promise the chained operation is resolved with.
     */
    const enqueueCameraTask = useCallback((task) => {
        const next = cameraQueueRef.current.then(task, task);
        cameraQueueRef.current = next.catch(() => {});

        return next;
    }, []);

    /**
     * Stops the camera and releases the preview.
     */
    const stopScan = useCallback(() => enqueueCameraTask(async () => {
        const scanner = scannerRef.current;
        scannerRef.current = null;

        if (!scanner) {
            return;
        }

        try {
            await scanner.stop();
            scanner.clear();
        } catch (error) {
            // The camera was already stopped, nothing else to release.
        }
    }), [enqueueCameraTask]);

    /**
     * Opens the detail a folio is read in, the one its type is registered with.
     *
     * @param {number} type Type of the QR code the folio is identified with.
     * @param {string} id Identifier of the folio.
     */
    const openFolio = (type, id) => {
        // The detail returns to the scanner, which keeps the view it was opened from
        navigate(`${ROUTE_BY_QR_CODE_TYPE[type]}${id}`, {
            state: {
                from: routes.materialRequest.scanner,
                backUrl: { pathname: routes.materialRequest.scanner, state: location.state },
            },
        });
    };

    /**
     * Resolves a scanned QR code and redirects to the view of its type.
     *
     * @param {string} decodedText Raw text read from the camera.
     */
    const onScanSuccess = async (decodedText) => {
        if (processingRef.current) {
            return;
        }

        processingRef.current = true;
        const qrBody = readQrCodeBody(decodedText);

        if (!qrBody) {
            processingRef.current = false;
            toast.error(t('invalid_qr_code'));
            return;
        }

        setStatus(SCANNER_STATUS.RESOLVING);
        setShowSpinner(true);
        await stopScan();

        dispatch(DecryptMessage(qrBody.data)).then(({ payload }) => {
            setShowSpinner(false);
            setStatus(SCANNER_STATUS.IDLE);
            processingRef.current = false;
            const { data, success, code } = payload?.data || {};

            if (success !== true) {
                errorMsg(code);
                return;
            }

            const id = typeof data === 'string' ? data.trim() : '';

            if (!UUID_REGEX.test(id)) {
                toast.error(t('invalid_qr_code'));
                return;
            }

            openFolio(qrBody.type, id);
        });
    };

    /**
     * Starts the camera and begins looking for a QR code.
     */
    const startScan = () => enqueueCameraTask(async () => {
        // The preview is only mounted while the module is open, so a start that
        // was queued before leaving it no longer has a region to be shown in.
        if (scannerRef.current || !document.getElementById(SCANNER_REGION_ID)) {
            return;
        }

        try {
            const scanner = new Html5Qrcode(SCANNER_REGION_ID);
            scannerRef.current = scanner;
            await scanner.start({ facingMode: 'environment' }, SCANNER_CONFIG, onScanSuccess, undefined);
            // A stop queued behind this start would otherwise remove the preview
            // while it is still being played.
            await waitForPreviewPlaying();
            setStatus(SCANNER_STATUS.SCANNING);
        } catch (error) {
            scannerRef.current = null;
            setStatus(SCANNER_STATUS.BLOCKED);
            toast.info(t('please_grant_permissions_to_use_the_camera'));
        }
    });

    /**
     * Opens the folio the gate writes by hand.
     *
     * The camera is released while the code is being written, so a QR code read
     * in the meantime does not leave the view the folio is being looked for in.
     */
    const handleOpenManualFolio = async () => {
        setSelectedFolio(null);
        setShowManualFolio(true);
        setStatus(SCANNER_STATUS.IDLE);
        await stopScan();
    };

    /**
     * Leaves the folio the gate was looking for and returns to the camera.
     */
    const handleCloseManualFolio = async () => {
        setShowManualFolio(false);
        setSelectedFolio(null);
        await startScan();
    };

    /**
     * Opens the detail the selected folio is read in.
     *
     * The search only offers the folios the QR code is issued for, so the folio
     * is opened with the type its detail is registered with, the same one the QR
     * code would have carried.
     */
    const handleOpenRequest = () => {
        if (!selectedFolio?.id) {
            return;
        }

        setShowManualFolio(false);

        openFolio(
            isVehicleMovement(selectedFolio) ? QR_CODE_TYPE.VEHICLE_MOVEMENT : QR_CODE_TYPE.MATERIAL_CONTROL,
            selectedFolio.id
        );
    };

    /**
     * Get current button/status configuration based on the active status.
     */
    const currentAction = SCANNER_STATUS_ACTIONS[status];

    // The camera starts on its own so the guard only points the device to the QR,
    // and it is released when the user leaves the module.
    useEffect(() => {
        startScan();

        return () => {
            stopScan();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return {
        t,
        status,
        currentAction,
        showSpinner,
        showManualFolio,
        selectedFolio,
        setSelectedFolio,
        handleOpenManualFolio,
        handleCloseManualFolio,
        handleOpenRequest,
        // Going back through the history would return to the detail view after a scan, so
        // the scanner returns to the view it was opened from, the material request list by default.
        goBack: () => navigate(location.state?.backUrl || routes.materialRequest.main),
    };
};
